// src/Modules/Log/Data/repositories/LogRepositoryImpl.ts
import { AxiosResponse } from 'axios';
import { Log, LogPagination, LogSummary } from '../../Model/Log';
import {
  LogParams,
  LogRepository,
} from '../../Domain/Repositories/LogRepository';
import { api } from '../../../../api/axiosInstance';

type LogsResponse = Log[] | LogPagination;

const isPaginatedResponse = (data: LogsResponse): data is LogPagination =>
  !Array.isArray(data) && Array.isArray(data.results);

export class LogRepositoryImpl implements LogRepository {
  private http = api;

  private normalizeParams(params: LogParams = {}) {
    const normalized = { ...params };

    if (normalized.pageSize && !normalized.page_size) {
      normalized.page_size = normalized.pageSize;
    }

    delete normalized.pageSize;

    return normalized;
  }

  getLogs(params: LogParams = {}) {
    const normalizedParams = this.normalizeParams(params);

    return this.http
      .get<LogsResponse>('/logs/', { params: normalizedParams })
      .then((r: AxiosResponse<LogsResponse>) =>
        isPaginatedResponse(r.data) ? r.data.results : r.data,
      );
  }

  getLogsPage(params: LogParams = {}) {
    const normalizedParams = this.normalizeParams(params);
    const fallbackPage = normalizedParams.page ?? 1;
    const fallbackPageSize = normalizedParams.page_size ?? 20;

    return this.http
      .get<LogsResponse>('/logs/', { params: normalizedParams })
      .then((r: AxiosResponse<LogsResponse>) => {
        if (isPaginatedResponse(r.data)) {
          return r.data;
        }

        return {
          count: r.data.length,
          total_pages: 1,
          current_page: fallbackPage,
          page_size: fallbackPageSize,
          results: r.data,
        };
      });
  }

  getLogsSummary(params: LogParams = {}) {
    const normalizedParams = this.normalizeParams(params);

    return this.http
      .get<LogSummary>('/logs/summary/', { params: normalizedParams })
      .then((r: AxiosResponse<LogSummary>) => r.data);
  }

  deleteLog(logId: number): Promise<void> {
    return this.http
      .delete(`/logs/${logId}/delete_log/`)
      .then(() => {});
  }

  bulkDeleteLogs(logIds: number[]): Promise<void> {
    return this.http
      .post('/logs/bulk_delete/', { log_ids: logIds })
      .then(() => {});
  }

  updateLogWithoutRecord(logId: number, payload: Partial<Pick<Log, 'action' | 'description' | 'final_days' | 'end_date' | 'total_days' | 'start_date'>>): Promise<Log> {
    return this.http
      .patch<Log>(`/logs/${logId}/update_log/`, payload)
      .then((r: AxiosResponse<Log>) => r.data);
  }

  downloadExcel(): Promise<Blob> {
    return this.http
      .get('/logs_excel_download/', { responseType: 'blob' })
      .then((r: AxiosResponse<Blob>) => r.data);
  }
}
